
<template>
  <DefaultLayout
    :pageTitle="meta.title"
    :pageDescription="meta.description"
  >
    <section
      class="content-section smb-blueprint__hero mt-20 sm:mt-40"
      data-gtm-context="Ebook"
    >
      <div class="container-fluid">
        <div class="row between-xs middle-xs">
          <div class="col-xs-12 col-sm-6 mb-10 sm:mb-0">
            <h1 class="mb-5 smb-blueprint__title">
              The SMB marketing blueprint
            </h1>
            <p class="p--lg mb-10 smb-blueprint__subtitle">
              How to improve visibility, stay consistent, and win repeat customers - without a marketing team
            </p>
            <button
              type="button"
              class="btn btn--primary"
              @click="openDownloadModal"
            >Download the guide</button>
          </div>
          <div class="col-xs-12 col-sm-6 mb-10 sm:mb-0">
            <picture class="smb-blueprint__cover">
              <source
                :srcset="require('@/assets/images/ebooks/smb-marketing-blueprint-hero.webp')"
                type="image/webp"
              />
              <img
                src="@/assets/images/ebooks/smb-marketing-blueprint-hero.png"
                alt="The SMB marketing blueprint cover"
                width="1024"
                height="1024"
              />
            </picture>
          </div>
        </div>
      </div>
    </section>

    <div class="divider--dashed"></div>

    <section
      class="content-section smb-blueprint__content mt-20"
      data-gtm-context="Ebook"
    >
      <div class="container-fluid">
        <div class="row center-xs text-left">
          <div class="col-xs-12 col-md-8">
            <h2 class="smb-blueprint__body-title">
              Marketing without a marketing team
            </h2>
            <p class="p--lg">
              If you're running a business, marketing may feel like something
              that's out of reach - particularly if you're working with limited
              budget, resources and expertise.
            </p>
            <p class="p--lg">
              While a hefty budget, large team and an encyclopedic knowledge of
              marketing undoubtedly helps, these are by no means essential.
            </p>
            <p class="p--lg">
              Our guide, 'The SMB marketing blueprint: How to improve
              visibility, stay consistent, and win repeat customers - without a
              specialist team' is your jargon-free guide showing how to improve
              visibility, stay consistent and win repeat customers with modest
              budgets, limited knowledge - and without hiring a single marketer.
            </p>

            <section class="key-stats mt-10" aria-label="Key statistics">
              <template v-for="(fact, index) in facts" :key="index">
                <div class="divider--dashed divider--no-margin"></div>
                <div class="key-stats__row">
                  <span class="mb-0 key-stats__percentage">{{ fact.value }}</span>
                  <p class="key-stats__text">{{ fact.text }}</p>
                </div>
              </template>
              <div class="divider--dashed divider--no-margin"></div>
            </section>
          </div>
        </div>

        <div class="divider--dashed"></div>

        <div class="row center-xs">
          <div class="col-xs-12 col-md-8">
            <h2 class="h4">In this guide, you'll learn:</h2>
          </div>
        </div>
        <ol class="smb-blueprint__chapters mt-8">
          <li
            v-for="(learning, index) in learnings"
            :key="index"
            class="smb-blueprint__chapter"
          >
            <span class="smb-blueprint__chapter-number">{{
              String(index + 1).padStart(2, "0")
            }}</span>
            <p class="mb-0">{{ learning }}</p>
          </li>
        </ol>

        <div class="row center-xs mt-16">
          <div class="col-xs-12 col-md-6">
            <p class="p--lg">
              We’ve even included a practical roadmap that brings everything together. No convoluted theory. No confusing buzzwords. Just practical steps, real-world examples, and clear action points at the end of each chapter, so you can move at your own pace and start seeing results straight away.
            </p>
            <button
              type="button"
              class="btn btn--primary mt-8"
              @click="openDownloadModal"
            >Download the guide</button>
          </div>
        </div>
      </div>
    </section>

    <teleport to="body">
      <div
        v-if="showDownloadModal"
        class="smb-blueprint-modal-overlay"
        @click.self="closeDownloadModal"
      >
        <div
          class="smb-blueprint-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Download the guide"
        >
          <button
            type="button"
            class="smb-blueprint-modal__close"
            aria-label="Close"
            data-gtm-ignore="true"
            @click="closeDownloadModal"
          >×</button>
          <div class="smb-blueprint-modal__content" ref="formContainer"></div>
        </div>
      </div>
    </teleport>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import { gtmSubmit, gtmTracking } from "@/functions/gtm-tracking";

export default {
  name: "SmbMarketingBlueprint",
  components: {
    DefaultLayout,
  },
  data() {
    return {
      meta: {
        title: "The SMB marketing blueprint - Transpond",
        description:
          "A jargon-free guide showing how to improve visibility, stay consistent and win repeat customers with modest budgets, limited knowledge - and without hiring a single marketer.",
      },
      showDownloadModal: false,
      facts: [
        {
          value: "46%",
          text: "46% to 50% operate without a documented marketing strategy",
        },
        {
          value: "73%",
          text: "73% of SMBs lack confidence in their marketing efforts",
        },
        {
          value: "14%",
          text: "Over 14% of small business failures are directly linked to poor marketing",
        },
      ],
      learnings: [
        "Why proactive marketing - not reactive marketing - is the way forward",
        "How to use customer data to shape your marketing strategy",
        "Why you don't need a designer to build professional emails",
        "How to automate your marketing",
        "Proven emails that attract and retain customers (with ready-to-use examples)",
        "Which marketing metrics you need to pay attention to",
        "How to add SMS and social media to your marketing mix - without increasing your workload",
        "Seven steps from zero to a working marketing system",
      ],
    };
  },
  watch: {
    showDownloadModal(isOpen) {
      if (isOpen) {
        this.disableBodyScroll();
        this.$nextTick(() => this.loadForm());
      } else {
        this.enableBodyScroll();
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onEscape);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onEscape);
    this.teardownFormListener();
    this.enableBodyScroll();
  },
  methods: {
    openDownloadModal() {
      gtmTracking("CTA Click", "Download the guide", "Ebook")();
      this.showDownloadModal = true;
    },
    closeDownloadModal() {
      this.teardownFormListener();
      this.showDownloadModal = false;
    },
    onEscape(event) {
      if (event.key === "Escape" && this.showDownloadModal) {
        this.closeDownloadModal();
      }
    },
    onLeadFormSubmit() {
      gtmSubmit("Lead Form", "The SMB marketing blueprint", "Ebook")();
    },
    loadForm() {
      const container = this.$refs.formContainer;
      if (!container || container.dataset.formLoaded) return;

      const script = document.createElement("script");
      script.src = "https://api.ourtransactionalemails.com/form?am=6&fid=62900";
      script.async = true;
      script.defer = true;
      script.type = "text/javascript";
      container.appendChild(script);
      container.dataset.formLoaded = "true";
      container.addEventListener("submit", this.onLeadFormSubmit, true);
    },
    teardownFormListener() {
      const container = this.$refs.formContainer;
      if (!container) return;
      container.removeEventListener("submit", this.onLeadFormSubmit, true);
    },
    disableBodyScroll() {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    },
    enableBodyScroll() {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    },
  },
};
</script>
